import { createContext, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompleteActivityModal } from '__components/Modals/ActivityCompletionModal/ActivityCompletionModal';
import useModal from '__components/Modals/useModal';
import { toastVariant, useToast } from '__components/ToastProvider';
import {
  FAILED_COMPLETE_ACTIVITY,
  LOG_ACTIVITY_SUCCESS,
} from '__pages/Common/activityHelpers';
import { useLoggableActivitiesQuery } from 'queries/models/activities-ts';
import { FORCE_ALL_RECORDS_SIZE } from 'services/helpers';
import ActivityService from '__services/ActivityService';
import { getOriginalError } from '__services/AxiosService';
import { useQueryClient } from '@tanstack/react-query';
import { ACTIVITIES } from 'queries/query-keys';

type LogActivityCtx = {
  openLogActivityModal: (activityId: string) => void;
};

type LogActivityProps = {
  children: any;
  enabled: boolean;
};

type Activity = {
  created: string;
  id: string;
  isEditable: boolean;
  name: string;
};

type LogActivityRequest = {
  activityObjectId: string;
  dirtyFields: boolean;
  payload: {
    fields: any[];
    mentions: any[];
    notes: string;
    relatedObjects: any[];
    scheduledActivityId?: string;
  };
};

const context = createContext<LogActivityCtx>({
  openLogActivityModal: () => {},
});

export const useLogActivityContext = () => {
  return useContext(context);
};

export const LogActivityContext = ({ children, enabled }: LogActivityProps) => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const [data, setData] = useState<Activity | null>(null);
  const [loggingActivity, setLoggingActivity] = useState(false);

  const loggableActivitiesQuery = useLoggableActivitiesQuery({
    params: { page_size: FORCE_ALL_RECORDS_SIZE },
    queryOptions: {
      enabled,
      select: (data) => {
        return new Set(data.results.map((activity) => activity.id));
      },
    },
  });

  const loggableActivityIds = loggableActivitiesQuery.data;

  const queryClient = useQueryClient();

  const handleLogActivity = async (req: LogActivityRequest) => {
    const { activityObjectId, payload } = req;
    setLoggingActivity(true);
    await ActivityService.v2CompleteActivity({
      activityObjectId,
      payload,
    });
    showToast({
      message: LOG_ACTIVITY_SUCCESS(t),
      variant: toastVariant.SUCCESS,
    });
    queryClient.invalidateQueries({
      queryKey: ACTIVITIES.LOGGED_ACTIVITY(activityObjectId),
    });
    setData(null);
    setLoggingActivity(false);
  };

  const handleLogActivityError = (error: any) => {
    const orig = getOriginalError(error);
    showToast({
      message: orig?.message || FAILED_COMPLETE_ACTIVITY(t),
      variant: toastVariant.FAILURE,
    });
    setLoggingActivity(false);
  };

  const [modalProps, , { show: showModal }] = useModal({
    handleSubmit: handleLogActivity,
    handleError: handleLogActivityError,
  });

  const openLogActivityModal = useCallback(
    async (id: string) => {
      try {
        const res = await ActivityService.v2GetActivity({
          activityObjectId: id,
        });

        if (!res.enabled) {
          showToast({
            message: t('Activity has been disabled'),
            variant: toastVariant.FAILURE,
          });
          return;
        }

        setData(res);
      } catch (err: any) {
        if (err?.response?.status === 404) {
          showToast({
            message: t('Activity was deleted'),
            variant: toastVariant.FAILURE,
          });
        }
        return;
      }

      if (loggableActivityIds && !loggableActivityIds.has(id)) {
        showToast({
          message: t(
            'You do not have access to this activity. Please contact your Kizen administrator.'
          ),
          variant: toastVariant.FAILURE,
        });
        setData(null);
      } else {
        showModal();
      }
    },
    [loggableActivityIds, showModal, showToast, t]
  );

  return (
    <context.Provider value={{ openLogActivityModal }}>
      {children}
      {enabled ? (
        <CompleteActivityModal
          {...modalProps}
          activity={{ activityObject: data }}
          disabled={loggingActivity}
        />
      ) : null}
    </context.Provider>
  );
};
